import {
    HomePageLayoutBlueprint,
    HomePageWidgetBlueprint,
    type HomePageLayoutProps,
  } from '@backstage/plugin-home-react/alpha';
  import { createFrontendModule } from '@backstage/frontend-plugin-api';
  import { Box, Grid, makeStyles } from '@material-ui/core';
  import {
    HomePageCompanyLogo,
    HomePageToolkit,
    HomePageStarredEntities,
  } from '@backstage/plugin-home';
  import { Content, Page, DismissableBanner } from '@backstage/core-components';
  import { HomePageSearchBar } from '@backstage/plugin-search';
  import { Typography, Link } from '@material-ui/core';
  import { LogoFull } from '../../components/logos/LogoFull';
  import {
    IconBrandSlack,
    IconBrandGithub,
    IconBrandAws,
    IconBrandDocker,
  } from '@tabler/icons-react';
  import tokens from '@cfa/design-tokens';

  const useLogoStyles = makeStyles(theme => ({
    container: {
      margin: theme.spacing(5, 0),
      width: '100%',
      height: 100,
      display: 'flex',
      justifyContent: 'center',
      fill: tokens.color.primary.base.modes.light,
    },
  }));

  const useSearchBarStyles = makeStyles(theme => ({
    searchBarRoot: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: '50px',
      boxShadow: theme.shadows[1],
      display: 'flex',
      margin: 'auto',
      maxWidth: '60vw',
    },
    searchBarOutline: {
      border: 'none',
    },
  }));

  const tools = [
    {
      url: 'https://cfaclouddevops.slack.com/archives/C1ZEYAU87',
      label: 'DevEx Support Channel',
      icon: <IconBrandSlack />,
    },
    {
      url: 'https://github.com/cfacorp/example-petstore',
      label: 'Example Petstore Repo',
      icon: <IconBrandGithub />,
    },
    {
      url: '/catalog?filters%5Bkind%5D=resource&filters%5Btype%5D=aws-account&filters%5Buser%5D=all',
      label: 'AWS Accounts List',
      icon: <IconBrandAws />,
    },
    {
      url: 'https://cfa.jfrog.io/',
      label: 'Artifactory',
      icon: <IconBrandDocker />,
    },
    {
      url: '/catalog?filters%5Bkind%5D=resource&filters%5Btype%5D=github-repository&filters%5Buser%5D=all',
      label: 'GitHub Repos List',
      icon: <IconBrandGithub />,
    }
  ];

  const IntroCard = () => (
    <Typography component="div">
      <Typography gutterBottom style={{ fontSize: '16px' }}>
        Homestead is Chick-fil-A's internal developer portal, designed to simplify
        the management of internal applications and services within our
        organization. It offers a centralized hub that enables developers, product
        managers, and other stakeholders to discover, collaborate on, and maintain
        the different tools and services we use.
      </Typography>
      <Typography gutterBottom>
        Homestead is built on the{' '}
        <Link
          color="primary"
          variant="body1"
          underline="hover"
          href="https://backstage.io"
          style={{ fontWeight: 'bold' }}
        >
          Backstage Platform
        </Link>
        , allowing teams to integrate internal applications and services using a
        plugin architecture.
      </Typography>
      <Typography gutterBottom>
        We are still porting plugins from{' '}
        <Link
          color="primary"
          variant="body1"
          underline="hover"
          href="https://backstage.app.cfahome.com"
          style={{ fontWeight: 'bold' }}
        >
          the previous iteration of our Internal Developer Portal
        </Link>
        , which will continue to be available until all functionality has been
        migrated.
      </Typography>
    </Typography>
  );

  function HomePageLayout({ widgets }: HomePageLayoutProps) {
    const logoClasses = useLogoStyles();
    const searchClasses = useSearchBarStyles();

    const shouldShowBanner = new Date() <= new Date('2025-09-14');

    const introWidget = widgets.find(w => w.name === 'home-page-widget:home/intro');
    if (!introWidget) {
      throw new Error('Intro widget not found');
    }
    const usefulLinksWidget = widgets.find(w => w.name === 'home-page-widget:home/useful-links');
    if (!usefulLinksWidget) {
      throw new Error('Useful Links widget not found');
    }
    const favoritesWidget = widgets.find(w => w.name === 'home-page-widget:home/favorites');
    if (!favoritesWidget) {
      throw new Error('Favorites widget not found');
    }

    return (
      <Page themeId="home">
        <Content>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
              {shouldShowBanner && (
                <DismissableBanner
                  variant="info"
                  message="An inital CFA Dark theme has been added. Please check your settings to choose your preferred theme."
                  id="themesettings202508281a"
                />
              )}
              <HomePageCompanyLogo
                className={logoClasses.container}
                logo={
                  <div
                    style={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <LogoFull size="md" variant="h2" centered />
                  </div>
                }
              />
            </Grid>
            <Grid container item xs={12} alignItems="center" direction="row">
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: searchClasses.searchBarRoot,
                    notchedOutline: searchClasses.searchBarOutline,
                  },
                }}
                placeholder="Search"
              />
            </Grid>
            <Box padding={5}>
              <Grid container item xs={12} alignItems="flex-start">
                <Grid item xs={8}>
                  {introWidget.component}
                </Grid>
                <Grid container item xs={4}>
                    <Grid item xs={12}>
                    {favoritesWidget.component}

                  </Grid>
                <Grid item xs={12}>
                {usefulLinksWidget.component}
                </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Content>
      </Page>
    );
  }

  const homeLayout = HomePageLayoutBlueprint.make({
    params: {
      loader: async () => HomePageLayout,
    },
  });

  const introWidget = HomePageWidgetBlueprint.make({
    name: 'intro',
    params: {
      title: 'What is Homestead?',
      components: async () => ({ Content: IntroCard }),
    },
  });

  const PassthroughRenderer = ({ Content: C, ...rest }: any) => <C {...rest} />;

  const favoritesWidget = HomePageWidgetBlueprint.make({
    name: 'favorites',
    params: {
      title: 'Favorites',
      components: async () => ({
        Content: (props: any) => <HomePageStarredEntities title="Favorites" {...props} />,
        Renderer: PassthroughRenderer,
      }),
    },
  });

  const usefulLinksWidget = HomePageWidgetBlueprint.make({
    name: 'useful-links',
    params: {
      title: 'Useful links',
      components: async () => ({
        Content: (props: any) => <HomePageToolkit {...props} />,
        Renderer: PassthroughRenderer,
      }),
      componentProps: { tools },
    },
  });

  export const homeModule = createFrontendModule({
    pluginId: 'home',
    extensions: [
      homeLayout,
      introWidget, 
      favoritesWidget, 
      usefulLinksWidget,
    ],
  });
